import {Page} from "@/PageDescriptors";
import PersonalExtensionList from "../../components/PersonalExtensionList";
import PersonalExtensionBreadcrumb from "../../components/breadcrumbs/PersonalExtension";
import SpeedDialsList from "../../components/extension/speed-dials/SpeedDialsList";
import {Devices} from "../../components/extension/devices/Devices";
import UpdateExtensionFormPage from "@/components/extension/settings/UpdateExtensionFormPage";
import {extensionFieldsetUser} from "@/components/extension/settings/fieldSets";
import UpdateExtensionProfilesPage from "@/components/extension/profiles/UpdateExtensionProfilesPage";
import {LimitedNotificationEmailWidget} from "@/components/extension/settings/LimitedNotificationEmailWidget";
import {PersonalAddressBookPage} from "@/components/address-book/PersonalAddressBookPage";
import SupervisedCallQueues from "@/components/personal_extensions/SupervisedCallQueues";
import QueueStatistics from "@/components/call-group/statistics/QueueStatistics";
import AgentStatistics from "@/components/call-group/statistics/AgentStatistics";
import QueueCallListPage from "@/components/call-group/statistics/QueueCallListPage";
import {EndpointBreadcrumb} from "../domains/tools";
import CallHistory from "@/components/personal_extensions/CallHistory";
import {CallGroupMembersPage} from "@/components/call-group/members/CallGroupMembers";
import {Auth} from "@/context/AuthProvider";
import {useStructureConfig} from "@/context/StructureConfigProvider";
import {useParams} from "react-router";
import {useExtensionCapabilityQuery} from "./queries.generated";
import {Capability} from "@/api";
import {BasePath} from "@/basePaths";
import {defineMessage} from "react-intl";

function useHideAddressBook(): boolean {
    const config = useStructureConfig();
    return !config.isEnabledService("ldap");
}

export const personalAddressBookPage: Page = {
    path: "address-book",
    label: defineMessage({id: "addressBook", defaultMessage: "Address book"}),
    contentComponent: (matchParams: {[s: string]: any}) => (
        <PersonalAddressBookPage extensionId={matchParams.extensionId} />
    ),
    hideHook: useHideAddressBook,
};

function useHideCallQueues(): boolean {
    const {extensionId} = useParams();
    const {loading, data} = useExtensionCapabilityQuery({
        variables: {
            userId: parseInt(extensionId),
        },
    });
    if (loading) return true;
    return !data.userCapabilities.includes(Capability.CcSupervisor);
}

const callQueues: Page = {
    path: "call-queues",
    label: defineMessage({id: "call_queue.section-title", defaultMessage: "Call queues"}),
    contentComponent: (props: {[s: string]: any}) => <SupervisedCallQueues extensionId={props.extensionId} />,
    hideHook: useHideCallQueues,
    items: [
        {
            path: ":queueId",
            label: null,
            breadcrumbComponent: EndpointBreadcrumb("groups", "queueId"),
            items: [
                {
                    path: "statistics",
                    label: defineMessage({
                        id: "call_queue.statistics.section-label",
                        defaultMessage: "Queue Statistics",
                    }),
                    contentComponent: (props: {[s: string]: any}) => <QueueStatistics groupId={props.queueId} />,
                },
                {
                    path: "agent-statistics",
                    label: defineMessage({
                        id: "call_queue.statistics.agent-section-label",
                        defaultMessage: "Agent Statistics",
                    }),
                    contentComponent: (props: {[s: string]: any}) => <AgentStatistics groupId={props.queueId} />,
                },
                {
                    path: "members",
                    label: defineMessage({id: "members", defaultMessage: "Members"}),
                    contentComponent: (props: {[s: string]: any}) => <CallGroupMembersPage groupId={props.queueId} />,
                },
                {
                    path: "calls",
                    label: defineMessage({id: "call_queue.call-list.section-label", defaultMessage: "$calls"}),
                    contentComponent: (props: {[s: string]: any}) => <QueueCallListPage groupId={props.queueId} />,
                },
            ],
        },
    ],
};

function useHidePersonalExtensions(auth: Auth): boolean {
    return auth.personalExtensionIdentifiers.length == 0;
}

const personalExtensionsPage: Page<BasePath> = {
    path: "personal-extensions",
    label: defineMessage({id: "personalExtensions", defaultMessage: "Personal users"}),
    contentComponent: PersonalExtensionList,
    hideHook: useHidePersonalExtensions,
    items: [
        {
            path: ":extensionId",
            breadcrumbComponent: PersonalExtensionBreadcrumb,
            label: null,
            items: [
                {
                    path: "profiles",
                    label: defineMessage({id: "profiles", defaultMessage: "Profiles"}),
                    contentComponent: (props: {[s: string]: any}) => (
                        <UpdateExtensionProfilesPage extensionId={props.extensionId} restrictedAccess />
                    ),
                },
                {
                    path: "devices",
                    label: defineMessage({id: "devices.title", defaultMessage: "Devices"}),
                    contentComponent: (props: {[s: string]: any}) => (
                        <Devices extensionId={props.extensionId} domainId={props.domainId} />
                    ),
                },
                {
                    path: "call-settings",
                    label: defineMessage({id: "callSettings", defaultMessage: "Call settings"}),
                    contentComponent: (props: {[s: string]: any}) => (
                        <UpdateExtensionFormPage
                            id={props.extensionId}
                            fields={extensionFieldsetUser}
                            customWidgetRenderers={{
                                notificationEmail: LimitedNotificationEmailWidget,
                            }}
                            fetchSecondStage={false}
                        />
                    ),
                },
                personalAddressBookPage,
                {
                    path: "speed-dials",
                    label: defineMessage({id: "speedDials.title", defaultMessage: "Speed dials"}),
                    contentComponent: (props: {[s: string]: any}) => <SpeedDialsList extensionId={props.extensionId} />,
                },
                {
                    path: "call-history",
                    label: defineMessage({id: "call_history", defaultMessage: "Call history"}),
                    contentComponent: (props: {[s: string]: any}) => <CallHistory extensionId={props.extensionId} />,
                },
                callQueues,
            ],
        },
    ],
};

export default personalExtensionsPage;
